import React from 'react';
import { View, Heading, Text, ScrollView } from '@aws-amplify/ui-react';
import { Helmet } from 'react-helmet-async';

const TermsOfUsePage: React.FC = () => {
  return (
    <View style={{ backgroundColor: 'white', minHeight: '100vh' }}>
      <Helmet>
        <title>Terms of Use - Banjara App</title>
      </Helmet>
      <ScrollView>
        <View padding="2rem" maxWidth="1200px" margin="0 auto">
          <Heading level={1} marginBottom="1rem" color="#000000">TERMS OF USE</Heading>
          <Text marginBottom="2rem" color="#333333">Last updated: April 04, 2025</Text>

          <View 
            style={{
              backgroundColor: "#f8f9fa",
              padding: "1rem",
              borderLeftStyle: "solid",
              borderLeftWidth: "4px",
              borderLeftColor: "#3B82F6",
              marginBottom: "2rem"
            }}
          >
            <Text color="#333333">
              Welcome to Banjara. These Terms of Use govern your use of the Banjara app, website, and services. 
              By using Banjara, you agree to be bound by these Terms.
            </Text>
          </View>

          <Heading level={2} marginTop="2rem" color="#000000">1. ACCEPTANCE OF TERMS</Heading>
          <Text marginBottom="1rem" color="#333333">
            By accessing or using our services, whether as a registered user or a guest, you agree to these Terms of Use ("Terms").
            If you do not agree to all the terms and conditions of this agreement, you may not access or use our services.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">2. CHANGES TO TERMS</Heading>
          <Text marginBottom="1rem" color="#333333">
            We may revise these Terms from time to time. The most current version will always be posted on our website.
            By continuing to access or use our Services after revisions become effective, you agree to be bound by the revised Terms.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">3. CREATING AN ACCOUNT</Heading>
          <Text marginBottom="1rem" color="#333333">
            To use most features of the Services, you must register for an account. You agree to provide accurate, current, and complete
            information during the registration process and to update such information to keep it accurate, current, and complete.
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">3.1 Account Security</Heading>
          <Text marginBottom="1rem" color="#333333">
            You are responsible for safeguarding your account credentials and for any activities or actions under your account.
            You agree to notify us immediately of any unauthorized access to or use of your username or password or any other breach of security.
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">3.2 Age Requirements</Heading>
          <Text marginBottom="1rem" color="#333333">
            You must be at least 18 years old to create an account and use our Services. By creating an account and using our Services,
            you represent and warrant that you are at least 18 years old.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">4. USER CONTENT</Heading>
          <Text marginBottom="1rem" color="#333333">
            Our services allow you to upload, store, send, and share content such as photos, text, and other materials ("User Content").
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">4.1 Your Rights to User Content</Heading>
          <Text marginBottom="1rem" color="#333333">
            You retain all rights to your User Content. By uploading or sharing User Content, you grant us a non-exclusive, royalty-free,
            worldwide license to use, display, and distribute your User Content solely for the purpose of providing the Services to you
            and those you choose to share with.
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">4.2 Content Restrictions</Heading>
          <Text marginBottom="0.5rem" color="#333333">You may not upload, store, or share any User Content that:</Text>
          <ul style={{ marginBottom: '1rem', paddingLeft: '2rem', color: '#333333' }}>
            <li>Violates any applicable law or regulation</li>
            <li>Infringes on the intellectual property rights of others</li>
            <li>Contains software viruses or any other malicious code</li>
            <li>Is defamatory, obscene, offensive, or harassing</li>
            <li>Includes personal information of minors</li>
            <li>Promotes illegal activities or contains hateful content</li>
          </ul>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">4.3 Content Monitoring</Heading>
          <Text marginBottom="1rem" color="#333333">
            We do not actively monitor or review User Content, but we reserve the right to review, remove,
            or disable access to any User Content that violates these Terms or is otherwise objectionable.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">5. ALBUM SHARING AND PERMISSIONS</Heading>
          <Text marginBottom="1rem" color="#333333">
            Banjara allows you to create and share albums with other users through invitation links.
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">5.1 Album Ownership</Heading>
          <Text marginBottom="1rem" color="#333333">
            When you create an album, you are designated as the "Creator" of that album and have full control over that album,
            including the ability to add, delete, or modify content, invite others, and delete the album.
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">5.2 User Roles</Heading>
          <Text marginBottom="0.5rem" color="#333333">Albums may have users with different permission levels:</Text>
          <ul style={{ marginBottom: '1rem', paddingLeft: '2rem', color: '#333333' }}>
            <li><strong>Creator:</strong> Has full control of the album</li>
            <li><strong>Participant:</strong> Can view, add, and delete their own photos</li>
            <li><strong>Viewer:</strong> Can only view photos in the album</li>
          </ul>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">5.3 Invitation Links</Heading>
          <Text marginBottom="1rem" color="#333333">
            You may generate invitation links to share albums with others. You are responsible for whom you share these
            links with. Invitation links expire after 24 hours for security purposes.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">6. STORAGE AND SUBSCRIPTIONS</Heading>
          <Text marginBottom="1rem" color="#333333">
            Banjara offers different storage tiers for users with different needs.
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">6.1 Free Tier</Heading>
          <Text marginBottom="1rem" color="#333333">
            Free users can view shared albums but receive no personal storage space. To upload your own content, 
            you will need to upgrade to a paid subscription.
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">6.2 Paid Subscriptions</Heading>
          <Text marginBottom="1rem" color="#333333">
            Paid subscription tiers provide additional storage space and features. Subscriptions are billed on a recurring
            basis until canceled.
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">6.3 Payment Processing</Heading>
          <Text marginBottom="1rem" color="#333333">
            For web subscriptions, payments are processed through our third-party payment processor, Stripe. Your use of Stripe's services
            is subject to their terms of service and privacy policy.
          </Text>
          <Text marginBottom="1rem" color="#333333">
            For mobile app subscriptions purchased through the Apple App Store, all billing is handled by Apple according to Apple's Terms of Service. 
            We do not have access to your Apple payment information.
          </Text>
          
          <Heading level={3} marginTop="1.5rem" color="#000000">6.4 Cancellation</Heading>
          <Text marginBottom="1rem" color="#333333">
            You may cancel your subscription at any time. For Apple App Store subscriptions, cancellation must be done through your Apple ID account settings. 
            Upon cancellation, your subscription will remain active until the end of the current billing period, after which your account will revert to the free tier.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">7. INTELLECTUAL PROPERTY RIGHTS</Heading>
          <Text marginBottom="1rem" color="#333333">
            Banjara and its original content, features, and functionality are owned by Banjara and are protected by international
            copyright, trademark, patent, trade secret, and other intellectual property laws.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">8. TERMINATION</Heading>
          <Text marginBottom="1rem" color="#333333">
            We may terminate or suspend your account and access to the Services immediately, without prior notice or liability,
            for any reason, including if you breach these Terms.
          </Text>
          
          <Text marginBottom="1rem" color="#333333">
            Upon termination, your right to use the Services will immediately cease. If you wish to terminate your account,
            you may simply discontinue using the Services or delete your account through the user settings.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">9. PRIVACY</Heading>
          <Text marginBottom="1rem" color="#333333">
            Your privacy is important to us. Our Privacy Policy, available at{' '}
            <a href="/privacy-policy" style={{ color: "#3B82F6", textDecoration: "none" }}>
              https://www.banjara-app.com/privacy-policy
            </a>, explains how we collect, use, and disclose information about you. By using our Services, you agree to the
            collection and use of information in accordance with our Privacy Policy.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">10. APPLE APP STORE TERMS (MINTERMS COMPLIANCE)</Heading>
          <Text marginBottom="1rem" color="#333333">
            If you downloaded this app from the Apple App Store, the following terms apply in accordance with Apple's minimum terms requirements:
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.1 Acknowledgement (Minterm 1)</Heading>
          <Text marginBottom="1rem" color="#333333">
            You and we acknowledge that this End-User License Agreement (EULA) is concluded between you and us only, and not with Apple, 
            and we, not Apple, are solely responsible for the Licensed Application and the content thereof. This EULA does not provide for 
            usage rules for Licensed Applications that are in conflict with the Apple Media Services Terms and Conditions.
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.2 Scope of License (Minterm 2)</Heading>
          <Text marginBottom="1rem" color="#333333">
            The license granted to you for the Licensed Application is limited to a non-transferable license to use the Licensed Application 
            on any Apple-branded products that you own or control and as permitted by the Usage Rules set forth in the Apple Media Services 
            Terms and Conditions, except that such Licensed Application may be accessed and used by other accounts associated with the purchaser 
            via Family Sharing or volume purchasing.
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.3 Maintenance and Support (Minterm 3)</Heading>
          <Text marginBottom="1rem" color="#333333">
            We are solely responsible for providing any maintenance and support services with respect to the Licensed Application, as specified 
            in this EULA, or as required under applicable law. You and we acknowledge that Apple has no obligation whatsoever to furnish any 
            maintenance and support services with respect to the Licensed Application.
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.4 Warranty (Minterm 4)</Heading>
          <Text marginBottom="1rem" color="#333333">
            We are solely responsible for any product warranties, whether express or implied by law, to the extent not effectively disclaimed. 
            In the event of any failure of the Licensed Application to conform to any applicable warranty, you may notify Apple, and Apple will 
            refund the purchase price for the Licensed Application to you; and to the maximum extent permitted by applicable law, Apple will have 
            no other warranty obligation whatsoever with respect to the Licensed Application, and any other claims, losses, liabilities, damages, 
            costs or expenses attributable to any failure to conform to any warranty will be our sole responsibility.
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.5 Product Claims (Minterm 5)</Heading>
          <Text marginBottom="1rem" color="#333333">
            You and we acknowledge that we, not Apple, are responsible for addressing any claims of yours or any third party relating to the 
            Licensed Application or your possession and/or use of that Licensed Application, including, but not limited to: (i) product liability 
            claims; (ii) any claim that the Licensed Application fails to conform to any applicable legal or regulatory requirement; and (iii) claims 
            arising under consumer protection, privacy, or similar legislation. This EULA does not limit our liability to you beyond what is permitted 
            by applicable law.
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.6 Intellectual Property Rights (Minterm 6)</Heading>
          <Text marginBottom="1rem" color="#333333">
            You and we acknowledge that, in the event of any third party claim that the Licensed Application or your possession and use of that 
            Licensed Application infringes that third party's intellectual property rights, we, not Apple, will be solely responsible for the 
            investigation, defense, settlement and discharge of any such intellectual property infringement claim.
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.7 Legal Compliance (Minterm 7)</Heading>
          <Text marginBottom="1rem" color="#333333">
            You represent and warrant that (i) you are not located in a country that is subject to a U.S. Government embargo, or that has been 
            designated by the U.S. Government as a "terrorist supporting" country; and (ii) you are not listed on any U.S. Government list of 
            prohibited or restricted parties.
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.8 Developer Name and Address (Minterm 8)</Heading>
          <Text marginBottom="1rem" color="#333333">
            Our name and address, and the contact information to which any End-User questions, complaints or claims with respect to the Licensed 
            Application should be directed, are provided in Section 17 (Contact Us) of these Terms.
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.9 Third Party Terms of Agreement (Minterm 9)</Heading>
          <Text marginBottom="1rem" color="#333333">
            You must comply with applicable third party terms of agreement when using the Application. For example, if you use the Application 
            in a way that requires wireless data services, you must not be in violation of your wireless data service agreement when using the Application.
          </Text>

          <Heading level={3} marginTop="1.5rem" color="#000000">10.10 Third Party Beneficiary (Minterm 10)</Heading>
          <Text marginBottom="1rem" color="#333333">
            You and we acknowledge and agree that Apple, and Apple's subsidiaries, are third party beneficiaries of this EULA, and that, upon your 
            acceptance of the terms and conditions of this EULA, Apple will have the right (and will be deemed to have accepted the right) to enforce 
            this EULA against you as a third party beneficiary thereof.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">11. DISCLAIMERS</Heading>
          <Text marginBottom="1rem" color="#333333">
            Your use of the Services is at your sole risk. The Services are provided on an "AS IS" and "AS AVAILABLE" basis.
            We expressly disclaim all warranties of any kind, whether express or implied, including but not limited to the
            implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
          </Text>
          
          <Text marginBottom="1rem" color="#333333">
            We make no warranty that (i) the Services will meet your requirements, (ii) the Services will be uninterrupted,
            timely, secure, or error-free, (iii) the results that may be obtained from the use of the Services will be accurate
            or reliable, or (iv) any errors in the Services will be corrected.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">12. LIMITATION OF LIABILITY</Heading>
          <Text marginBottom="1rem" color="#333333">
            In no event shall Banjara, its directors, employees, partners, agents, suppliers, or affiliates, be liable for any
            indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data,
            use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use
            the Services; (ii) any conduct or content of any third party on the Services; (iii) any content obtained from the Services;
            and (iv) unauthorized access, use or alteration of your transmissions or content, whether based on warranty, contract,
            tort (including negligence) or any other legal theory, whether or not we have been informed of the possibility of such damage.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">13. INDEMNIFICATION</Heading>
          <Text marginBottom="1rem" color="#333333">
            You agree to defend, indemnify, and hold harmless Banjara, its affiliates, licensors, and service providers, and its and
            their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from
            and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable
            attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Services.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">14. GOVERNING LAW</Heading>
          <Text marginBottom="1rem" color="#333333">
            These Terms shall be governed by and construed in accordance with the laws of the State of Washington, without regard
            to its conflict of law provisions.
          </Text>
          
          <Text marginBottom="1rem" color="#333333">
            Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
            If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of
            these Terms will remain in effect.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">15. DISPUTE RESOLUTION</Heading>
          <Text marginBottom="1rem" color="#333333">
            Any dispute arising from or relating to the subject matter of these Terms shall be finally settled by arbitration
            in Seattle, Washington, using the English language in accordance with the Arbitration Rules and Procedures of the
            Judicial Arbitration and Mediation Services, Inc. ("JAMS") then in effect, by one commercial arbitrator with
            substantial experience in resolving intellectual property and commercial contract disputes.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">16. ENTIRE AGREEMENT</Heading>
          <Text marginBottom="1rem" color="#333333">
            These Terms constitute the entire agreement between you and Banjara regarding our Services and supersede any prior
            agreements between you and Banjara relating to the Services.
          </Text>

          <Heading level={2} marginTop="2rem" color="#000000">17. CONTACT US</Heading>
          <Text marginBottom="0.5rem" color="#333333">If you have any questions about these Terms, please contact us at:</Text>
          <Text marginBottom="1rem" color="#333333">
            <strong>Banjara</strong><br />
            Email: <a href="mailto:support@banjara-app.com" style={{ color: "#3B82F6", textDecoration: "none" }}>support@banjara-app.com</a>
          </Text>
          <Text marginBottom="2rem" color="#333333">
            For questions, complaints, or claims specifically related to the mobile application, please contact us at the above email address.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
};

export default TermsOfUsePage;